import {
  SlidersHorizontal,
  ChartNoAxesColumn,
  CircleDollarSign,
  ClipboardList,
  Contact,
  Receipt,
  UserPlus,
} from "lucide-react";
import { useState } from "react";
import { useGetIdentity, useStore } from "ra-core";

import { useConfigurationContext } from "../../atomic-crm/root/ConfigurationContext";
import { DashboardActivityLog } from "../../atomic-crm/dashboard/DashboardActivityLog";
import { TasksList } from "../../atomic-crm/dashboard/TasksList";
import { MetricCard } from "../data-display/MetricCard";
import { DateRangeSelector, type DashboardRange } from "./DateRangeSelector";
import { HotLeadsCard } from "./HotLeadsCard";
import { PipelineOverviewCard } from "./PipelineOverviewCard";
import { QuickActionsMenu } from "./QuickActionsMenu";
import { computeMetrics, useDashboardData } from "./useDashboardMetrics";

const greetingForHour = (hour: number) => {
  if (hour < 12) return "Good morning";
  if (hour < 18) return "Good afternoon";
  return "Good evening";
};

/**
 * The Gravun dashboard. Every number is computed from real workspace data
 * (RLS-scoped); metrics whose modules don't exist yet show ", ".
 */
export const GravunDashboard = () => {
  const { data: identity } = useGetIdentity();
  const { currency, dealPipelineStatuses } = useConfigurationContext();
  const [range, setRange] = useStore<DashboardRange>(
    "gravun.dashboard.range",
    "month",
  );
  // Per-user widget visibility (ra-core store persists per browser profile,
  // the same mechanism the range picker already uses). Role-appropriate data
  // is enforced BELOW this layer: metrics a role cannot read render ", "
  // because RLS returns nothing, so hiding is preference, never security.
  const [hidden, setHidden] = useStore<string[]>("gravun.dashboard.hidden", []);
  const show = (key: string) => !hidden.includes(key);
  const toggle = (key: string) =>
    setHidden(
      hidden.includes(key)
        ? hidden.filter((item) => item !== key)
        : [...hidden, key],
    );

  const { deals, invoices, payments, leadsAdded, totalContacts, tasksDue } =
    useDashboardData(range);
  const metrics = computeMetrics(
    deals,
    invoices,
    payments,
    leadsAdded,
    totalContacts,
    tasksDue,
    range,
    currency,
    dealPipelineStatuses,
  );

  const firstName = identity?.fullName?.split(" ")[0];
  const today = new Date().toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-[23px] font-semibold leading-8 tracking-tight text-foreground">
            {greetingForHour(new Date().getHours())}
            {firstName ? `, ${firstName}` : ""}
          </h1>
          <p className="text-[13px] text-muted-foreground">{today}</p>
        </div>
        <div className="flex items-center gap-2">
          <DateRangeSelector value={range} onChange={setRange} />
          <CustomizeMenu hidden={hidden} onToggle={toggle} />
          <QuickActionsMenu />
        </div>
      </div>

      {show("metrics") && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
          <MetricCard
            label="Pipeline Value"
            value={metrics.pipelineValue.value}
            helper={metrics.pipelineValue.helper}
            icon={ChartNoAxesColumn}
          />
          <MetricCard
            label="Revenue"
            value={metrics.revenue.value}
            helper={metrics.revenue.helper}
            icon={CircleDollarSign}
          />
          <MetricCard
            label="Outstanding"
            value={metrics.outstanding.value}
            helper={metrics.outstanding.helper}
            icon={Receipt}
          />
          <MetricCard
            label="New Leads"
            value={metrics.leadsAdded.value}
            helper={metrics.leadsAdded.helper}
            icon={UserPlus}
          />
          <MetricCard
            label="Contacts"
            value={metrics.contacts.value}
            helper={metrics.contacts.helper}
            icon={Contact}
          />
          <MetricCard
            label="Tasks Due"
            value={metrics.tasksDue.value}
            helper={metrics.tasksDue.helper}
            icon={ClipboardList}
          />
        </div>
      )}

      {show("hot_leads") && <HotLeadsCard />}

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
        {show("pipeline") && <PipelineOverviewCard deals={deals} />}
        {show("tasks") && (
          <div className="[&_h2]:text-[14px] [&_h2]:font-semibold [&_h2]:tracking-tight [&_h2]:text-foreground">
            <TasksList />
          </div>
        )}
      </div>

      {show("activity") && (
        <div className="[&_h2]:text-[14px] [&_h2]:font-semibold [&_h2]:tracking-tight [&_h2]:text-foreground">
          <DashboardActivityLog />
        </div>
      )}
    </div>
  );
};

const DASHBOARD_WIDGETS: { key: string; label: string }[] = [
  { key: "metrics", label: "KPI cards" },
  { key: "hot_leads", label: "Hot leads" },
  { key: "pipeline", label: "Pipeline overview" },
  { key: "tasks", label: "Tasks" },
  { key: "activity", label: "Recent activity" },
];

/** Choose which sections this dashboard shows, per user, instantly undoable. */
const CustomizeMenu = ({
  hidden,
  onToggle,
}: {
  hidden: string[];
  onToggle: (key: string) => void;
}) => {
  const [open, setOpen] = useState(false);
  return (
    <span className="relative">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="flex h-8 items-center gap-1 rounded-md border border-input px-2.5 text-[12.5px] text-muted-foreground hover:bg-accent hover:text-foreground"
      >
        <SlidersHorizontal className="size-3.5" />
        Customize
      </button>
      {open ? (
        <span className="absolute right-0 top-9 z-20 flex w-48 flex-col gap-1.5 rounded-md border border-border bg-popover p-2 shadow-md">
          {DASHBOARD_WIDGETS.map((widget) => (
            <label
              key={widget.key}
              className="flex items-center gap-2 text-[12.5px]"
            >
              <input
                type="checkbox"
                checked={!hidden.includes(widget.key)}
                onChange={() => onToggle(widget.key)}
              />
              {widget.label}
            </label>
          ))}
        </span>
      ) : null}
    </span>
  );
};
